<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { Category } from '../../types'
import * as api from '../../api/tecdocApi'
import { SHORTCUT_ICONS, DEFAULT_ICON, type ShortcutIconCode } from './shortcutIcons'

const props = defineProps<{
  carId: number
}>()

const emit = defineEmits<{
  (e: 'save', payload: { categoryId: number; label: string; iconCode: string }): void
  (e: 'close'): void
}>()

const selectedIconCode = ref<ShortcutIconCode>(DEFAULT_ICON)

type BreadcrumbEntry = { id: number; name: string }

const path = ref<BreadcrumbEntry[]>([{ id: 0, name: 'Alle kategorier' }])
const categories = ref<Category[]>([])
const isLoading = ref(false)
const loadError = ref<string | null>(null)

const selected = ref<{ categoryId: number; label: string } | null>(null)
const editableLabel = ref<string>('')
const saving = ref(false)
const saveError = ref<string | null>(null)

const currentParentId = computed(() => path.value[path.value.length - 1].id)

// TecDoc's assemblyGroupFacets returns the full set of assembly groups relevant
// to the filtered article set — this includes the parent itself AND ancestors
// further up the tree. Without filtering, drilling two levels deep shows the
// grand-parent again and clicking it looks like an infinite loop.
// Exclude the current node (self) and every node already in the breadcrumb
// (ancestors), and also drop already-visited duplicates via a Set.
const visibleCategories = computed<Category[]>(() => {
  const ancestorIds = new Set(path.value.map(p => p.id))
  const seen = new Set<number>()
  const result: Category[] = []
  for (const c of categories.value) {
    if (ancestorIds.has(c.categoryId)) continue
    if (seen.has(c.categoryId)) continue
    seen.add(c.categoryId)
    result.push(c)
  }
  return result
})

async function loadLevel(parentId: number) {
  isLoading.value = true
  loadError.value = null
  try {
    categories.value = await api.getCategories(props.carId, parentId)
  } catch (err) {
    loadError.value = (err as Error).message || 'Kunne ikke laste kategorier'
    categories.value = []
  } finally {
    isLoading.value = false
  }
}

function drillInto(cat: Category) {
  path.value.push({ id: cat.categoryId, name: cat.categoryName })
  loadLevel(cat.categoryId)
}

function jumpToPath(index: number) {
  if (index >= path.value.length - 1) return
  path.value = path.value.slice(0, index + 1)
  loadLevel(path.value[index].id)
}

function pickCategory(cat: Category) {
  selected.value = { categoryId: cat.categoryId, label: cat.categoryName }
  editableLabel.value = cat.categoryName
  saveError.value = null
}

async function confirmSave() {
  if (!selected.value) return
  const label = editableLabel.value.trim()
  if (!label) {
    saveError.value = 'Etikett kan ikke være tom'
    return
  }
  saving.value = true
  saveError.value = null
  try {
    emit('save', {
      categoryId: selected.value.categoryId,
      label,
      iconCode: selectedIconCode.value,
    })
  } finally {
    saving.value = false
  }
}

function pickIcon(code: ShortcutIconCode) {
  selectedIconCode.value = code
}

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) {
    emit('close')
  }
}

onMounted(() => {
  loadLevel(0)
})
</script>

<template>
  <div class="picker-modal" @click="onBackdropClick">
    <div class="picker-modal__dialog" role="dialog" aria-label="Velg kategori">
      <header class="picker-modal__header">
        <h3>Velg kategori å feste</h3>
        <button type="button" class="picker-modal__close" @click="emit('close')">×</button>
      </header>

      <nav class="picker-modal__breadcrumb">
        <template v-for="(step, idx) in path" :key="step.id + '-' + idx">
          <button
            v-if="idx < path.length - 1"
            type="button"
            class="picker-modal__crumb picker-modal__crumb--link"
            @click="jumpToPath(idx)"
          >
            {{ step.name }}
          </button>
          <span
            v-else
            class="picker-modal__crumb picker-modal__crumb--current"
          >
            {{ step.name }}
          </span>
          <span v-if="idx < path.length - 1" class="picker-modal__crumb-sep">›</span>
        </template>
      </nav>

      <div class="picker-modal__body">
        <div v-if="isLoading" class="picker-modal__state">Laster…</div>
        <div v-else-if="loadError" class="picker-modal__state picker-modal__state--error">
          {{ loadError }}
        </div>
        <ul v-else-if="visibleCategories.length" class="picker-modal__list">
          <li
            v-for="cat in visibleCategories"
            :key="cat.categoryId"
            class="picker-modal__row"
            :class="{ 'picker-modal__row--selected': selected?.categoryId === cat.categoryId }"
          >
            <button
              type="button"
              class="picker-modal__row-main"
              @click="cat.childCount > 0 ? drillInto(cat) : pickCategory(cat)"
            >
              <span class="picker-modal__row-name">{{ cat.categoryName }}</span>
              <span class="picker-modal__row-meta">
                <span class="picker-modal__row-id">#{{ cat.categoryId }}</span>
                <span v-if="cat.articleCount" class="picker-modal__row-count">
                  {{ cat.articleCount }} deler
                </span>
                <span v-if="cat.childCount > 0" class="picker-modal__row-arrow">›</span>
              </span>
            </button>
            <button
              type="button"
              class="picker-modal__row-pick"
              title="Velg denne kategorien"
              @click.stop="pickCategory(cat)"
            >
              ✓
            </button>
          </li>
        </ul>
        <div v-else class="picker-modal__state">Ingen kategorier her</div>
      </div>

      <footer v-if="selected" class="picker-modal__selection">
        <label class="picker-modal__label">
          Valgt: <strong>#{{ selected.categoryId }}</strong>
        </label>
        <input
          v-model="editableLabel"
          type="text"
          class="picker-modal__label-input"
          placeholder="Etikett"
          :disabled="saving"
        />
        <div class="picker-modal__icons-row">
          <span class="picker-modal__icons-label">Velg ikon:</span>
          <div class="picker-modal__icons-grid">
            <button
              v-for="iconDef in SHORTCUT_ICONS"
              :key="iconDef.code"
              type="button"
              class="picker-modal__icon-btn"
              :class="{ 'picker-modal__icon-btn--active': selectedIconCode === iconDef.code }"
              :title="iconDef.label"
              :disabled="saving"
              @click="pickIcon(iconDef.code)"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                v-html="iconDef.svgPath"
              />
            </button>
          </div>
        </div>
        <button
          type="button"
          class="picker-modal__save"
          :disabled="saving || !editableLabel.trim()"
          @click="confirmSave"
        >
          {{ saving ? 'Lagrer…' : 'Lagre' }}
        </button>
        <button
          type="button"
          class="picker-modal__cancel"
          :disabled="saving"
          @click="emit('close')"
        >
          Avbryt
        </button>
        <span v-if="saveError" class="picker-modal__error">{{ saveError }}</span>
      </footer>
      <footer v-else class="picker-modal__selection picker-modal__selection--empty">
        <span>Klikk en kategori for å velge den</span>
        <button type="button" class="picker-modal__cancel" @click="emit('close')">Avbryt</button>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.picker-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 6vh;
  z-index: 9999;

  &__dialog {
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    width: min(640px, 92vw);
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    h3 {
      margin: 0;
      font-size: 1rem;
      color: #111827;
    }
  }

  &__close {
    background: transparent;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
    color: #6b7280;

    &:hover { color: #111827; }
  }

  &__breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.8rem;
  }

  &__crumb {
    padding: 2px 6px;
    border-radius: 4px;
    border: none;
    background: transparent;
    cursor: default;
    color: #374151;

    &--link {
      cursor: pointer;
      color: #002868;
      text-decoration: underline;

      &:hover { background: #e0ecff; }
    }

    &--current {
      font-weight: 600;
      color: #111827;
    }
  }

  &__crumb-sep {
    color: #9ca3af;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    min-height: 200px;
  }

  &__state {
    padding: 2rem;
    text-align: center;
    color: #6b7280;

    &--error {
      color: #991b1b;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid #f3f4f6;

    &--selected {
      background: #e0ecff;
    }

    &:hover:not(&--selected) {
      background: #f9fafb;
    }
  }

  &__row-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    color: #111827;
    font-size: 0.9rem;
  }

  &__row-name {
    font-weight: 500;
  }

  &__row-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #6b7280;
    font-size: 0.75rem;
  }

  &__row-id {
    font-family: monospace;
  }

  &__row-arrow {
    font-size: 1.1rem;
    color: #9ca3af;
  }

  &__row-pick {
    padding: 0 0.75rem;
    background: transparent;
    border: none;
    border-left: 1px solid #f3f4f6;
    cursor: pointer;
    color: #16a34a;
    font-weight: 700;
    font-size: 1rem;

    &:hover {
      background: #dcfce7;
    }
  }

  &__selection {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;

    &--empty {
      justify-content: space-between;
      color: #6b7280;
      font-size: 0.85rem;
    }
  }

  &__label {
    font-size: 0.85rem;
    color: #374151;
  }

  &__icons-row {
    flex-basis: 100%;
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }

  &__icons-label {
    font-size: 0.85rem;
    color: #374151;
    padding-top: 0.375rem;
    flex-shrink: 0;
  }

  &__icons-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    flex: 1;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 5px;
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.1s, border-color 0.1s, color 0.1s;

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    &:hover:not(:disabled) {
      background: #f3f4f6;
      color: #002868;
    }

    &--active {
      background: #e0ecff;
      color: #002868;
      border-color: #002868;
      border-width: 2px;
      padding: 4px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__label-input {
    flex: 1;
    min-width: 180px;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  &__save {
    padding: 6px 16px;
    background: #002868;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: #001d4f;
    }
  }

  &__cancel {
    padding: 6px 14px;
    background: transparent;
    color: #4b5563;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }
  }

  &__error {
    flex-basis: 100%;
    color: #991b1b;
    font-size: 0.75rem;
  }
}
</style>
